import { HttpClient } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Router } from '@angular/router';
import { api_base_url } from '../api/api_url';
import { CartItem } from '../interfaces/cart-item';
import { Articolo, ArticoloFull } from '../interfaces/custom/articolo';
import { Documento } from '../interfaces/custom/documento';
import { getTipoSpedizioneFBStr } from '../interfaces/custom/spedizione';
import { AccountService } from './custom/account.service';
import { PriceArticoloService } from './custom/price-articolo.service';
import { StoreService } from './store.service';

declare let gtag : any;
declare let fbq : any;

const api_url_events = api_base_url + 'Events/ShopAnalytics/';

window['gtag'] = (...args) => {};
window['fbq'] = (...args) => {};

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private renderer2 : Renderer2;

    constructor(private router: Router, private store : StoreService, rendererFactory: RendererFactory2,
        private priceArticolo : PriceArticoloService, private account : AccountService, private http : HttpClient) {
        this.renderer2 = rendererFactory.createRenderer(null, null);
    }

    UA_ID;
    FB_PIXEL;

    loadGoogleAnalytics(UA_ID) {
        if (!UA_ID)
            return;

        this.UA_ID = UA_ID;

        window['dataLayer'] = window['dataLayer'] || [];
        window['gtag'] = function() {
            window['dataLayer'].push(arguments); 
        }
        gtag('js', new Date());
        gtag('set', { 'currency': 'EUR' });


        const s = this.renderer2.createElement('script');
        s.type = 'text/javascript';
        s.src = 'https://www.googletagmanager.com/gtag/js?id=' + UA_ID;
        s.async = 'true';
        s.text = ``;
        this.renderer2.appendChild(document.body, s);

        this.onGoogleAnalyticsPageView(document.location.pathname);
    }

    loadFacebookPixel(FB_PIXEL) {
        if (!FB_PIXEL)
            return;

        this.FB_PIXEL = FB_PIXEL;

        window['fbq'] = undefined;

        // @ts-ignore
        !function (f:any, b, e, v, n, t, s) {
            if (f.fbq) return; n = f.fbq = function () {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            };
            if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
            n.queue = []; t = b.createElement(e); t.async = !0;
            t.src = v; s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s)
        }(window, document, 'script',
            'https://connect.facebook.net/en_US/fbevents.js');


        fbq('init', FB_PIXEL);

        this.onFacebookPageView();
    }

    onGoogleAnalyticsPageView(url) {
        gtag('config', this.UA_ID,
            {
                'page_path': url
            }
        );
    }

    onFacebookPageView() {
        fbq('track', 'PageView');
    }

    onPageView(path) {
        this.onGoogleAnalyticsPageView(path);
        this.onFacebookPageView();
    }

    onAddToCart(item : CartItem) {
        gtag('event', 'add_to_cart', {
            "items": [
                {
                    "id": item.product.codart,
                    "name": item.product.descart,
                    "quantity": item.quantity,
                    "price": item.price
                }
            ],
            "value": item.quantity * item.price
        });

        fbq('track', 'AddToCart', {
            'content_ids': [item.product.codart],
            'content_name': item.product.descart,
            'content_type': 'product',
            'currency': 'EUR',
            'value': item.price,
        });

        this.mtAddToCart(item);
    }

    onAddToWishlist(product : ArticoloFull) {
        gtag('event', 'add_to_wishlist', {
            "items": [
                {
                    "id": product.codart,
                    "name": product.descart,
                    "price": this.priceArticolo.prezzoArticolo(product)
                }
            ],
            "value": this.priceArticolo.prezzoArticolo(product)
        });

        fbq('track', 'AddToWishlist', {
            'content_ids': [product.codart],
            'content_name': product.descart,
            'content_type': 'product',
            'currency': 'EUR',
            'value': this.priceArticolo.prezzoArticolo(product)
        });

        this.mtAddToPreferiti(product);
    }

    onConfirmRegistration() {
        fbq('track', 'CompleteRegistration', {
            'status': 'confirmed'
        });

        gtag("event", "sign_up", {
            method: 'email-confirmed'
        });
    }

    onRegister() {
        fbq('track', 'CompleteRegistration', {
            'status': 'registered'
        });

        gtag("event", "sign_up", {
            method: 'email'
        });
    }

    onLogin() {
        gtag('event', 'login', {
            method: 'email'
        });
    }

    onSearch(query) {
        fbq('track', 'Search', {
            'search_string': query
        });

        gtag('event', 'search', {
            'search_term': query
        });
    }

    onAddPaymentInfo(paymentInfo) {
        fbq('track', 'AddPaymentInfo');
        gtag('event', 'add_payment_info');

        gtag('event', 'set_checkout_option', {
            checkout_step: 1,
            checkout_option: paymentInfo.descrizione,
        });

        this.mtInfoPayment(paymentInfo);
    }

    onProductView(product : ArticoloFull) {
        gtag('event', 'view_item', {
            items: [{
              id: product.codart,
              name: product.descart,
            }]
          });
        
        this.mtViewArticolo(product);
    }

    // ON CHECKOUT START
    onCheckoutInit(cart) {
        fbq('track', 'InitiateCheckout', this.getFBcheckoutParams(cart));
        gtag('event', 'begin_checkout', this.getGAcheckoutParams(cart));

        this.mtCheckout(this.getMTCheckoutParams(cart));
    }

    getMTCheckoutParams(cart) {
        let value = 0;
        let contents = [];

        for (let item of cart.items) {
            let content = {};
            content["id"] = item["product"]["codart"];
            content["name"] = item["product"]["descart"];
            content["quantity"] = item.quantity;
            content["price"] = item.price;
            contents.push(content);
            value += item.quantity * item.price;
        }

        if (cart.shippingCost) {
            let content = {
                'id': `sped.${cart.shippingMethod.codice}`,
                'name': cart.shippingMethod.descrizione,
                'quantity': 1,
                'price': cart.shippingCost,
            }
            contents.push(content);
            value += cart.shippingCost;
        }

        let params = {
            'items': contents,
            'value': value,
        };

        params['delivery_category'] = getTipoSpedizioneFBStr(cart.shippingMethod.tipo_spedizione);
 
        return params;
    }

    getFBcheckoutParams(cart) {
        let num_items = 0;
        let value = 0;
        let contents = [];

        for (let item of cart.items) {
            let content = {};
            content["id"] = item["product"]["codart"];
            content["quantity"] = item.quantity;
            content["item_price"] = item.price;
            contents.push(content);
            num_items += item.quantity;
            value += item.quantity * item.price;
        }

        if (cart.shippingCost) {
            let content = {
                'id': `sped.${cart.shippingMethod.codice}`,
                'quantity': 1,
                'item_price': cart.shippingCost,
            }
            contents.push(content);
            num_items += 1;
            value += cart.shippingCost;
        }

        let params = {
            'content_type': 'product',
            'currency': 'EUR',
            'contents': contents,
            'num_items': num_items,
            'value': value,
        };

        let shippingType = cart.shippingMethod.tipo_spedizione

        if (shippingType == 0) {
            params['delivery_category'] = 'home_delivery';
        }
        else if (shippingType == 1) {
            params['delivery_category'] = 'in_store';
        }
        else if (shippingType == 2) {
            params['delivery_category'] = 'home_delivery';
        }
        return params;
    }

    getGAcheckoutParams(cart) {
        let value = 0;
        let contents = [];

        for (let item of cart.items) {
            let content = {};
            content["id"] = item["product"]["codart"];
            content["name"] = item["product"]["descart"];
            content["quantity"] = item.quantity;
            content["price"] = item.price;
            contents.push(content);
            value += item.quantity * item.price;
        }

        if (cart.shippingCost) {
            let content = {
                'id': `sped.${cart.shippingMethod.codice}`,
                'name': cart.shippingMethod.descrizione,
                'quantity': 1,
                'price': cart.shippingCost,
            }
            contents.push(content);
            value += cart.shippingCost;
        }

        let params = {
            'items': contents,
            'value': value,
        };
        return params;
    }

    // ON CHECKOUT END


    // ON PURCHASE START

    onPurchaseEvent(doc : Documento) {
        let fbParams = this.getFBpurchaseParams(doc);
        fbParams['order_id'] = doc.numreg;
        fbq('track', 'Purchase', fbParams);

        let googleParams = this.getGApurchaseParams(doc);
        googleParams["transaction_id"] = doc.numreg;
        gtag("event", "purchase", googleParams);

        let mtParams = this.getMTPurchaseParams(doc);
        mtParams['numreg'] = doc.numreg;
        this.mtPurchase(mtParams);
    }

    getMTPurchaseParams(doc : Documento) {
        let contents = [];

        for (let item of doc.corpo) {
            let content = {};
            content["id"] = item.codiceArticolo;
            content["name"] = item.descrizioneArticolo;
            content["quantity"] = item.qta;
            content["price"] = Number(item.totale_riga / item.qta).toFixed(2);

            if (item.tipoRiga == 4) {
                content["id"] =`sped.${doc.codice_spedizione}`;
                if (!item.prezzo)
                    continue;
            }

            contents.push(content);
        }

        let params = {
            'items': contents,
            'value': doc.totaleAPagare,
        };

        let shippingType = doc.tipo_spedizione;

        if (shippingType == 0) {
            params['delivery_category'] = 'Corriere';
        }
        else if (shippingType == 1) {
            params['delivery_category'] = 'Ritiro In Negozio';
        }
        else if (shippingType == 2) {
            params['delivery_category'] = 'Domicilio';
        }

        return params;
    }

    getFBpurchaseParams(doc : Documento) {
        let num_items = 0;
        let contents = [];

        for (let item of doc.corpo) {
            let content = {};
            content["id"] = item.codiceArticolo;
            content["quantity"] = item.qta;
            content["item_price"] = Number(item.totale_riga / item.qta).toFixed(2);

            if (item.tipoRiga == 4) {
                content["id"] =`sped.${doc.codice_spedizione}`;
                if (!item.prezzo)
                    continue;
            }

            contents.push(content);
            num_items += item.qta;
        }

        let params = {
            'content_type': 'product',
            'currency': 'EUR',
            'contents': contents,
            'num_items': num_items,
            'value': doc.totaleAPagare,
        };

        let shippingType = doc.tipo_spedizione;

        if (shippingType == 0) {
            params['delivery_category'] = 'home_delivery';
        }
        else if (shippingType == 1) {
            params['delivery_category'] = 'in_store';
        }
        else if (shippingType == 2) {
            params['delivery_category'] = 'home_delivery';
        }

        return params;
    }

    getGApurchaseParams(doc : Documento) {
        let contents = [];

        for (let item of doc.corpo) {
            let content = {};
            content["id"] = item.codiceArticolo;
            content["name"] = item.descrizioneArticolo;
            content["quantity"] = item.qta;
            content["price"] = Number(item.totale_riga / item.qta).toFixed(2);

            if (item.tipoRiga == 4) {
                content["id"] =`sped.${doc.codice_spedizione}`;
                if (!item.prezzo)
                    continue;
            }

            contents.push(content);
        }

        let params = {
            'items': contents,
            'value': doc.totaleAPagare,
        };

        return params;
    }

    // ON PURCHASE END


    // CUSTOM EVENTS
    get headers() {
        return {
            ...this.account.getHeader(),
            "Business": this.store.azienda_nome_univoco
        }
    }

    mtCheckout(dati) {
        this.http.post(api_url_events + 'Checkout', {dati}, {headers: this.headers}).subscribe();
    }

    mtInfoPayment(paymentInfo) {
        this.http.post(api_url_events + 'InfoPayment', 
        {"data": paymentInfo}, {headers: this.headers}).subscribe();
    }

    mtPurchase(dati) {
        this.http.post(api_url_events + 'Purchase', {dati}, {headers: this.headers}).subscribe();
    }

    mtAddToCart(item : CartItem) {
        let body = {
            "codice_articolo": item.product.codart,
            "option1": 0,
            "option2": 0,
            "option3": 0,
            "option4": 0,
            "dati": {
                "name": item.product.descart,
                "quantity": item.quantity,
                "price": item.price
            }
        };

        let options = item.options;
        if (options) {
            for (let i = 1; i <= 4; i++) {
                if (options[i]) {
                    body['option' + i] = options[i].option.progressivo;
                }
            }
        }

        this.http.post(api_url_events + 'AddToCart', body, {headers: this.headers}).subscribe();
    }

    mtAddToPreferiti(item : ArticoloFull) {
        this.http.post(api_url_events + 'AddToPreferiti', {
            "codice_articolo": item.codart,
            "option1": 0,
            "option2": 0,
            "option3": 0,
            "option4": 0,
            "dati": {
                "name": item.descart,
                "price": this.priceArticolo.prezzoArticolo(item)
            }
        }, {headers: this.headers}).subscribe();
    }

    mtRicerca(ricerca, numero_risultati) {
        this.http.post(api_url_events + 'Ricerca', {ricerca, numero_risultati}, {headers: this.headers}).subscribe();
    }

    mtViewArticolo(articolo : ArticoloFull) {
        this.http.post(api_url_events + 'articolo/1', [{"codart": articolo.codart}], {headers: this.headers}).subscribe();
    }

    mtImpressumArticoli(articoli : Articolo[]) {
        let data = articoli.map(articolo => ({"codart": articolo.codart}));
        this.http.post(api_url_events + 'articolo/0', data, {headers: this.headers}).subscribe();
    }

    mtViewAzienda() {
        this.http.get(api_url_events + 'Azienda', {headers: this.headers}).subscribe();
    }

}
